import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

const ROLES = [
  { value: "athlete", label: "Athlete" },
  { value: "team", label: "Team" },
  { value: "league", label: "League" },
  { value: "media", label: "Media" },
  { value: "partner", label: "Partner" },
  { value: "sponsor", label: "Sponsor" },
  { value: "other", label: "Other" },
] as const;

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  email: z.string().trim().email("Enter a valid email").max(320),
  phone: z.string().trim().max(50).optional().or(z.literal("")),
  role: z.string().refine(
    (v) => ["athlete", "team", "league", "media", "partner", "sponsor", "other"].includes(v),
    { message: "Select one option" },
  ),
  message: z.string().trim().min(1, "Message is required").max(5000),
});

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse({ name, email, phone, role, message });
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { fe[i.path[0] as string] = i.message; });
      setErrors(fe);
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      role: parsed.data.role,
      message: parsed.data.message,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Could not send", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Message sent", description: "Thanks — we'll be in touch soon." });
    setName(""); setEmail(""); setPhone(""); setRole(""); setMessage("");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-16 max-w-2xl">
        <header className="mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Contact</span>
          <h1 className="text-5xl md:text-6xl mt-3">Get in touch.</h1>
          <p className="text-muted-foreground mt-3">
            Tell us a little about who you are and how we can help.
          </p>
        </header>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} />
            {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
          </div>

          <div>
            <Label htmlFor="phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={50} />
            {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
          </div>

          <div>
            <Label>I am a…</Label>
            <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {ROLES.map((r) => (
                <label
                  key={r.value}
                  htmlFor={`role-${r.value}`}
                  className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:border-primary transition-colors"
                >
                  <RadioGroupItem id={`role-${r.value}`} value={r.value} />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </RadioGroup>
            {errors.role && <p className="text-sm text-destructive mt-1">{errors.role}</p>}
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              rows={6}
            />
            {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
          </div>

          <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
